import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, ProgressBar, Badge } from 'react-bootstrap';
import { FaFileDownload, FaCheckCircle, FaExclamationTriangle, FaTimesCircle } from 'react-icons/fa';

import 'shared-pages/pages/collections/grid/import-from-excel/import-progress.css';
import { ImportState, Level, LogEntry } from './useImportProgress';
import { downloadResultBlob } from './import-result-annotator';

export interface ImportProgressLabels {
  title?: string;
  close?: string;
  processed?: string;
  inserted?: string;
  updated?: string;
  errors?: string;
  noLogs?: string;
}

interface ImportProgressModalProps {
  show: boolean;
  onClose: () => void;
  state: ImportState;
  labels?: ImportProgressLabels;
}

const DEFAULT_LABELS: Required<ImportProgressLabels> = {
  title: 'Εισαγωγή Σημείων Συλλογής από Excel',
  close: 'Κλείσιμο',
  processed: 'Επεξεργασμένα',
  inserted: 'Νέα',
  updated: 'Ενημερώσεις',
  errors: 'Σφάλματα',
  noLogs: 'Δεν υπάρχουν μηνύματα ακόμα.',
};

const formatTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('el-GR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getLevelBadgeVariant = (level: Level): string => {
  switch (level) {
    case 'info':
      return 'info';
    case 'warn':
      return 'warning';
    case 'error':
      return 'danger';
    default:
      return 'secondary';
  }
};

const LogItem: React.FC<{ entry: LogEntry }> = ({ entry }) => (
  <div className={`ip-log-item lv-${entry.level} d-flex align-items-start`}>
    <span className="log-time">{formatTime(entry.ts)}</span>
    {entry.row && <span className="log-row">Γραμμή {entry.row}</span>}
    <Badge bg={getLevelBadgeVariant(entry.level)} className="me-2">
      {entry.level.toUpperCase()}
    </Badge>
    <span className="log-message">{entry.message}</span>
  </div>
);

const OutcomeSummary: React.FC<{
  outcome: NonNullable<ImportState['outcome']>;
  inserted: number;
  updated: number;
  errors: number;
}> = ({ outcome, inserted, updated, errors }) => {
  const [isExpanded, setIsExpanded] = useState(outcome.status !== 'success');

  const meta =
    outcome.status === 'success'
      ? {
          variant: 'success' as const,
          icon: <FaCheckCircle className="me-2" />,
          title: 'Εισαγωγή ολοκληρώθηκε επιτυχώς',
          className: 'ip-outcome-success',
        }
      : outcome.status === 'partial'
        ? {
            variant: 'warning' as const,
            icon: <FaExclamationTriangle className="me-2" />,
            title: 'Μερική επιτυχία',
            className: 'ip-outcome-partial',
          }
        : {
            variant: 'danger' as const,
            icon: <FaTimesCircle className="me-2" />,
            title: 'Δεν έγινε καταχώρηση',
            className: 'ip-outcome-empty',
          };

  const subtitle =
    outcome.status === 'success'
      ? `Νέα: ${inserted} · Ενημερώσεις: ${updated}`
      : outcome.status === 'partial'
        ? `${inserted} νέα · ${updated} ενημερώσεις · ${errors} σφάλματα`
        : outcome.reasons[0] || 'Καμία εγγραφή δεν καταχωρήθηκε ή ενημερώθηκε.';

  return (
    <div className={`ip-outcome-banner mb-3 ${meta.className}`}>
      <Button
        variant={`outline-${meta.variant}`}
        size="sm"
        className="ip-outcome-button d-flex align-items-center justify-content-between w-100"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="d-flex align-items-center flex-wrap">
          {meta.icon}
          <strong className="me-2">{meta.title}</strong>
          <span className="ip-outcome-subtitle">{subtitle}</span>
        </div>
        {(outcome.reasons.length > 0 || outcome.status !== 'success') && (
          <span className={`ip-outcome-chevron ${isExpanded ? 'expanded' : ''}`}>▼</span>
        )}
      </Button>

      {outcome.reasons.length > 0 && (
        <div className={`ip-outcome-details ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="ip-outcome-content">
            <div className="ip-outcome-label">Λεπτομέρειες:</div>
            <ul className="ip-outcome-reasons">
              {outcome.reasons.map((reason, i) => (
                <li key={i} className="ip-outcome-reason">
                  {reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const ResultFileCard: React.FC<{
  resultFile: NonNullable<ImportState['resultFile']>;
  errors: number;
}> = ({ resultFile, errors }) => {
  const needsReview = errors > 0;

  return (
    <div className={`ip-result-file-card mb-3 ${needsReview ? 'needs-review' : 'all-ok'}`}>
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div className="flex-grow-1">
          <div className="ip-result-file-title">
            <FaFileDownload className="me-2" />
            Αρχείο αποτελεσμάτων Excel
          </div>
          <p className="ip-result-file-desc mb-0">
            {needsReview
              ? 'Κατεβάστε το ενημερωμένο αρχείο: κάθε γραμμή έχει κατάσταση και ID σημείου ή λόγο αποτυχίας.'
              : 'Κατεβάστε το ενημερωμένο αρχείο με το ID κάθε σημείου συλλογής στη στήλη αποτελεσμάτων.'}
          </p>
          <div className="ip-result-file-hint text-muted mt-1">
            Στήλες: «Κατάσταση Εισαγωγής» · «Αποτέλεσμα Εισαγωγής»
          </div>
        </div>
        <Button
          variant={needsReview ? 'warning' : 'success'}
          size="sm"
          className="ip-result-download-btn flex-shrink-0"
          onClick={() => downloadResultBlob(resultFile.blob, resultFile.fileName)}
        >
          <FaFileDownload className="me-1" />
          Λήψη αποτελεσμάτων
        </Button>
      </div>
    </div>
  );
};

const ProgressStepper: React.FC<{
  steps: ImportState['steps'];
  onStepFilter?: (stepKey: string | null) => void;
  activeFilter?: string | null;
}> = ({ steps, onStepFilter, activeFilter }) => (
  <div className="ip-stepper" role="progressbar" aria-label="Βήματα εισαγωγής">
    {steps.map((step, index) => (
      <div
        key={step.key}
        className={`ip-step is-${step.status} ${activeFilter === step.key ? 'is-filtered' : ''}`}
        role="listitem"
        aria-label={`Βήμα ${index + 1}: ${step.label}`}
        onClick={() => onStepFilter?.(activeFilter === step.key ? null : step.key)}
        style={{ cursor: onStepFilter ? 'pointer' : 'default' }}
      >
        <div className="ip-bubble" aria-hidden="true">
          {step.status === 'done' ? '' : index + 1}
        </div>
        <div className="ip-label">
          {step.label}
          {step.count !== undefined && step.count > 0 && (
            <span className="ip-step-count">{step.count}</span>
          )}
        </div>
      </div>
    ))}
  </div>
);

const StatsRow: React.FC<{
  state: ImportState;
  labels: Required<ImportProgressLabels>;
}> = ({ state, labels }) => (
  <div className="ip-stats">
    <Badge bg="secondary">
      {labels.processed}: {state.processed}
    </Badge>
    <Badge bg="success">
      {labels.inserted}: {state.inserted}
    </Badge>
    <Badge bg="info">
      {labels.updated}: {state.updated}
    </Badge>
    <Badge bg="danger">
      {labels.errors}: {state.errors}
    </Badge>
  </div>
);

export const ImportProgressModal: React.FC<ImportProgressModalProps> = ({
  show,
  onClose,
  state,
  labels = {},
}) => {
  const mergedLabels = { ...DEFAULT_LABELS, ...labels };
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const finished = !state.running && !!state.outcome;

  const filteredLogs = activeFilter
    ? state.logs.filter((log) => log.meta?.stepKey === activeFilter)
    : state.logs;

  const lastLogId = filteredLogs[filteredLogs.length - 1]?.id;

  useEffect(() => {
    const container = logContainerRef.current;
    if (!show || !container || filteredLogs.length === 0) return;

    requestAnimationFrame(() => {
      const nearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 48;
      if (state.running || nearBottom) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }, [show, filteredLogs.length, lastLogId, state.running, activeFilter]);

  return (
    <Modal show={show} onHide={onClose} size="xl" backdrop centered>
      <Modal.Header className="ip-modal-header">
        <Modal.Title className="ip-modal-title">
          {mergedLabels.title}
          {state.worksheetName && (
            <span className="ip-worksheet-name">({state.worksheetName})</span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {finished && state.outcome && (
          <OutcomeSummary
            outcome={state.outcome}
            inserted={state.inserted}
            updated={state.updated}
            errors={state.errors}
          />
        )}

        {finished && state.resultFile && (
          <ResultFileCard resultFile={state.resultFile} errors={state.errors} />
        )}

        <StatsRow state={state} labels={mergedLabels} />

        <ProgressStepper
          steps={state.steps}
          onStepFilter={setActiveFilter}
          activeFilter={activeFilter}
        />

        <div className="ip-progress-container">
          <div className="ip-progress-label">
            <span>Πρόοδος</span>
            <span>{Math.round(state.progress)}%</span>
          </div>
          <ProgressBar
            now={state.progress}
            variant={'info'}
            animated={state.running}
            style={{ height: '24px', borderRadius: '4px' }}
          />
        </div>

        <div
          ref={logContainerRef}
          className="ip-log"
          aria-live="polite"
          aria-atomic="false"
          aria-label="Μηνύματα εισαγωγής"
        >
          {activeFilter && (
            <div className="ip-filter-info mb-2">
              <Badge bg="info" className="me-2">
                Φίλτρο: {state.steps.find((s) => s.key === activeFilter)?.label}
              </Badge>
              <Button variant="link" size="sm" className="p-0" onClick={() => setActiveFilter(null)}>
                Εμφάνιση όλων
              </Button>
            </div>
          )}
          {filteredLogs.length === 0 ? (
            <div className="text-muted text-center p-3" style={{ fontStyle: 'italic' }}>
              {activeFilter ? 'Δεν υπάρχουν μηνύματα για αυτό το βήμα.' : mergedLabels.noLogs}
            </div>
          ) : (
            filteredLogs.map((entry) => <LogItem key={entry.id} entry={entry} />)
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="ip-modal-footer">
        <div className="d-flex justify-content-between w-100 align-items-center gap-2 flex-wrap">
          <div>
            {finished && state.resultFile && (
              <Button
                variant="outline-success"
                size="sm"
                onClick={() =>
                  downloadResultBlob(state.resultFile!.blob, state.resultFile!.fileName)
                }
              >
                <FaFileDownload className="me-1" />
                Λήψη αποτελεσμάτων
              </Button>
            )}
          </div>
          <Button variant="secondary" onClick={onClose}>
            {mergedLabels.close}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
